import {useContext, useEffect} from "react";
import Analytics from "../utils/analyticsTracking";
import {usePrevious} from "./utility";
import {validateField} from "../utils/validation";
import {useDispatch} from "react-redux";
import {setGlobalLayoutProps, setPageSpecificProps} from "../components/Layout/actions";
import {GlobalContext} from "../hoc/LayoutContext";
import {pageTrackingData} from "../utils/utils";
import {getCLS, getFID, getLCP} from "web-vitals";

const trackPage = (page, options = {}) => {
    Analytics.set({
        page,
        ...options,
    });
    Analytics.pageview(page);
};
const logWebVitalsToGA = ({name, delta, id}) => {
    Analytics.event({
        category: "Web Vitals",
        action: name,
        label: id,
        value: Math.round(name === "CLS" ? delta * 1000 : delta),
        nonInteraction: true,
    });
};

function trackWebVitals() {
    getCLS(logWebVitalsToGA);
    getFID(logWebVitalsToGA);
    getLCP(logWebVitalsToGA);
}

const useBasePage = (location, globalLayoutProps, activePage, pageTrackingConfig) => {
    const prevLocation = usePrevious(location);
    const context = useContext(GlobalContext);
    const {updateContext} = context;
    const dispatch = useDispatch();
    // console.log(globalLayoutProps);
    useEffect(() => {
        let currentPage = location.pathname;
        let prevPage = validateField(prevLocation) ? prevLocation.pathname : "";

        if (location.search !== "") currentPage += location.search;
        if (typeof prevLocation !== "undefined" && prevLocation.search !== "")
            prevPage += prevLocation.search;
        if (currentPage !== prevPage) {
            trackPage(currentPage);
            trackWebVitals();
        }
    }, [location, prevLocation]);

    useEffect(() => {
        if (validateField(activePage) && window.serverRendered === false) {
            let pageSpecificTrackingObj = {};
            if (pageTrackingConfig && Object.keys(pageTrackingConfig).length > 0) {
                const {headerSignUp: headerSignUpMobile} = pageTrackingData(
                    true,
                    pageTrackingConfig,
                    activePage,
                    "headerSignUp",
                );
                const {headerSignUp: headerSignUpDesktop} = pageTrackingData(
                    false,
                    pageTrackingConfig,
                    activePage,
                    "headerSignUp",
                );

                const {googleOneTap: googleOneTapMobile} = pageTrackingData(
                    true,
                    pageTrackingConfig,
                    activePage,
                    "googleOneTap",
                );
                const {googleOneTap: googleOneTapDesktop} = pageTrackingData(
                    false,
                    pageTrackingConfig,
                    activePage,
                    "googleOneTap",
                );

                const {externalURL: externalURLSignUpMobile} = pageTrackingData(
                    true,
                    pageTrackingConfig,
                    activePage,
                    "externalURL",
                );
                const {externalURL: externalURLSignUpDesktop} = pageTrackingData(
                    false,
                    pageTrackingConfig,
                    activePage,
                    "externalURL",
                );

                if (validateField(headerSignUpMobile) && validateField(headerSignUpDesktop)) {
                    pageSpecificTrackingObj.currentSignUpTrackingKey = {
                        mobile: headerSignUpMobile.trackingId,
                        laptop: headerSignUpDesktop.trackingId,
                    };
                }

                if (validateField(googleOneTapMobile) && validateField(googleOneTapDesktop)) {
                    pageSpecificTrackingObj.currentGoogleOneTabSignUpTrackingKey = {
                        mobile: googleOneTapMobile.trackingId,
                        laptop: googleOneTapDesktop.trackingId,
                    };
                }

                if (
                    validateField(externalURLSignUpDesktop) &&
                    validateField(externalURLSignUpMobile)
                ) {
                    pageSpecificTrackingObj.currentExternalURLSignUpTrackingKey = {
                        mobile: externalURLSignUpMobile.trackingId,
                        laptop: externalURLSignUpDesktop.trackingId,
                    };
                }

                const pageGroupName = pageTrackingData(
                    false,
                    pageTrackingConfig,
                    activePage,
                    "",
                    "pageGroupName",
                );
                if (validateField(pageGroupName)) {
                    pageSpecificTrackingObj.currentPageGroupName = pageGroupName;
                }
            }
            pageSpecificTrackingObj.currentPageName = activePage;
            dispatch(setPageSpecificProps(pageSpecificTrackingObj));
        }
    }, [activePage, dispatch, pageTrackingConfig]);

    useEffect(() => {
        if (window.serverRendered === false) {
            dispatch(setGlobalLayoutProps(globalLayoutProps));
            if (!validateField(activePage)) {
                const {
                    pageName,
                    headerSignUpTrackingKey,
                    googleOneTabSignUpTrackingKey,
                    externalURLSignUpTrackingKey,
                } = globalLayoutProps;
                if (pageName) {
                    const trackingObj = {
                        currentPageName: pageName,
                        currentSignUpTrackingKey: headerSignUpTrackingKey,
                        currentGoogleOneTabSignUpTrackingKey: googleOneTabSignUpTrackingKey,
                        currentExternalURLSignUpTrackingKey: externalURLSignUpTrackingKey,
                    };
                    dispatch(setPageSpecificProps(trackingObj));
                }
            }
        }
        const handleScroll = () => {
            updateContext({isScrolled: true});
            window.removeEventListener("scroll", handleScroll);
        };
        window.addEventListener("scroll", handleScroll);
        return () => {
            updateContext({isScrolled: false});
            window.removeEventListener("scroll", handleScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalLayoutProps]);
};

export default useBasePage;
